import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal-page";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "ClaimGuard | Privacy Policy" },
      {
        name: "description",
        content:
          "How ClaimGuard collects, uses, and protects the information you share with us.",
      },
      { property: "og:title", content: "ClaimGuard | Privacy Policy" },
      {
        property: "og:description",
        content: "How we handle the information you share with ClaimGuard.",
      },
      { name: "robots", content: "index,follow" },
      { property: "og:url", content: "https://smart-invoice-ally.lovable.app/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://smart-invoice-ally.lovable.app/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Privacy Policy"
      updated="July 5, 2026"
      intro="This Privacy Policy explains what information ClaimGuard collects, why we collect it, how we use it, and the choices available to you. It applies to our website, product, and customer communications."
      footer="This page is maintained by ClaimGuard. It describes current practices for the customer-facing product and may be updated as the platform evolves."
      sections={[
        {
          heading: "Information you provide",
          body: (
            <p>
              Account details (name, email, password), invoices and supporting documents
              you upload, messages you send us, and payment information collected by our
              payment processor.
            </p>
          ),
        },
        {
          heading: "Information we collect automatically",
          body: (
            <p>
              Basic device, browser, and usage information (pages viewed, actions taken,
              approximate location derived from IP) to keep the service secure and to
              measure and improve product quality.
            </p>
          ),
        },
        {
          heading: "How we use information",
          body: (
            <>
              <p>We use information to:</p>
              <ul className="list-disc pl-5">
                <li>Provide invoice review, reports, and full-service negotiation.</li>
                <li>Process payments and prevent fraud.</li>
                <li>Respond to support requests and send transactional messages.</li>
                <li>Improve accuracy, reliability, and security of the platform.</li>
              </ul>
            </>
          ),
        },
        {
          heading: "Sharing and subprocessors",
          body: (
            <p>
              We use trusted service providers (hosting, database, payments, email,
              analytics, error monitoring, OCR, and location validation) that process
              information on our behalf under contract. We do not sell your personal
              information.
            </p>
          ),
        },
        {
          heading: "Data security",
          body: (
            <p>
              Access to your data is restricted by authentication and row-level access
              rules. Data is transmitted over TLS. No system is perfectly secure, and we
              encourage you to use a strong, unique password and to notify us promptly if
              you suspect unauthorized access.
            </p>
          ),
        },
        {
          heading: "Retention",
          body: (
            <p>
              We retain data as described in our{" "}
              <a className="text-trust underline" href="/data-retention">
                Data Retention Policy
              </a>
              . You may request deletion of your account and associated data at any time.
            </p>
          ),
        },
        {
          heading: "Your choices",
          body: (
            <p>
              You can update account details in Settings, request a copy of your data,
              request deletion, or opt out of non-essential email. Subject to applicable
              law, you may also have rights to access, correct, or restrict certain
              processing of your personal information.
            </p>
          ),
        },
        {
          heading: "Children",
          body: (
            <p>
              ClaimGuard is not directed to children under 16 and we do not
              knowingly collect personal information from them.
            </p>
          ),
        },
        {
          heading: "Contact",
          body: (
            <p>
              Privacy questions or requests can be sent to{" "}
              <a className="text-trust underline" href="mailto:privacy@invoiceintelligence.com">
                privacy@invoiceintelligence.com
              </a>
              .
            </p>
          ),
        },
      ]}
    />
  );
}
